import type { AppearanceId } from './deviceAppearance';

/** Stylized “live look” silhouettes (own drawings, not manufacturer photos). */
export function DeviceLiveLook({
  appearanceId,
  className,
}: {
  appearanceId: AppearanceId;
  className?: string;
}) {
  const common = {
    viewBox: '0 0 320 180',
    className,
    role: 'img' as const,
    'aria-hidden': true as const,
  };

  switch (appearanceId) {
    case 'mikrotik_rb2011':
      return (
        <svg {...common}>
          <rect className="live-look-bg" width="320" height="180" />
          <rect x="36" y="58" width="248" height="64" rx="6" fill="#4b5565" />
          <rect x="44" y="66" width="40" height="10" rx="2" fill="#98a2b3" />
          {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map((i) => (
            <rect key={i} x={96 + i * 17} y="78" width="14" height="14" rx="2" fill="#12b76a" />
          ))}
          <circle cx="58" cy="98" r="5" fill="#f04438" />
          <text x="160" y="150" textAnchor="middle" className="live-look-caption" fontSize="12">
            RB2011 · front ports
          </text>
        </svg>
      );
    case 'mikrotik_switch':
    case 'rack_switch':
      return (
        <svg {...common}>
          <rect className="live-look-bg" width="320" height="180" />
          <rect x="28" y="50" width="264" height="70" rx="5" fill="#344054" />
          {Array.from({ length: 16 }).map((_, i) => (
            <rect key={i} x={40 + i * 15} y="72" width="12" height="12" rx="1.5" fill="#32d583" />
          ))}
          <rect x="40" y="58" width="36" height="8" rx="2" fill="#98a2b3" />
          <text x="160" y="150" textAnchor="middle" className="live-look-caption" fontSize="12">
            Switch · 19″ look
          </text>
        </svg>
      );
    case 'mikrotik_hap':
      return (
        <svg {...common}>
          <rect className="live-look-bg" width="320" height="180" />
          <rect x="110" y="70" width="100" height="55" rx="10" fill="#475467" />
          <rect x="150" y="42" width="8" height="30" rx="3" fill="#98a2b3" />
          <rect x="162" y="36" width="8" height="36" rx="3" fill="#98a2b3" />
          <circle cx="130" cy="98" r="4" fill="#12b76a" />
          <text x="160" y="155" textAnchor="middle" className="live-look-caption" fontSize="12">
            Wi‑Fi router
          </text>
        </svg>
      );
    case 'access_point':
      return (
        <svg {...common}>
          <rect className="live-look-bg" width="320" height="180" />
          <ellipse cx="160" cy="108" rx="58" ry="18" fill="#344054" />
          <ellipse cx="160" cy="100" rx="52" ry="28" fill="#475467" />
          <circle cx="160" cy="96" r="10" fill="#98a2b3" />
          <path
            d="M132 72c16-18 40-18 56 0M140 62c12-12 28-12 40 0"
            fill="none"
            stroke="#98a2b3"
            strokeWidth="3"
            strokeLinecap="round"
          />
          <circle cx="186" cy="104" r="4" fill="#12b76a" />
          <text x="160" y="158" textAnchor="middle" className="live-look-caption" fontSize="12">
            Access point
          </text>
        </svg>
      );
    case 'firewall_appliance':
      return (
        <svg {...common}>
          <rect className="live-look-bg" width="320" height="180" />
          <rect x="50" y="55" width="220" height="70" rx="6" fill="#1d2939" />
          <rect x="66" y="72" width="50" height="10" rx="2" fill="#f04438" />
          {[0, 1, 2, 3].map((i) => (
            <rect key={i} x={140 + i * 28} y="80" width="18" height="14" rx="2" fill="#53b1fd" />
          ))}
          <text x="160" y="150" textAnchor="middle" className="live-look-caption" fontSize="12">
            Firewall appliance
          </text>
        </svg>
      );
    case 'tower_server':
      return (
        <svg {...common}>
          <rect className="live-look-bg" width="320" height="180" />
          <rect x="105" y="28" width="110" height="124" rx="6" fill="#344054" />
          {[0, 1, 2, 3].map((i) => (
            <rect key={i} x="118" y={42 + i * 24} width="84" height="16" rx="2" fill="#667085" />
          ))}
          <circle cx="190" cy="138" r="4" fill="#12b76a" />
          <text x="160" y="168" textAnchor="middle" className="live-look-caption" fontSize="12">
            Server
          </text>
        </svg>
      );
    case 'desktop_pc':
      return (
        <svg {...common}>
          <rect className="live-look-bg" width="320" height="180" />
          <rect x="70" y="40" width="110" height="80" rx="4" fill="#475467" />
          <rect x="78" y="48" width="94" height="58" rx="2" fill="#d0d5dd" />
          <rect x="110" y="122" width="30" height="18" fill="#98a2b3" />
          <rect x="90" y="140" width="70" height="6" rx="2" fill="#667085" />
          <rect x="200" y="70" width="50" height="70" rx="4" fill="#344054" />
          <text x="160" y="168" textAnchor="middle" className="live-look-caption" fontSize="12">
            Desktop PC
          </text>
        </svg>
      );
    case 'laptop':
      return (
        <svg {...common}>
          <rect className="live-look-bg" width="320" height="180" />
          <rect x="70" y="40" width="180" height="95" rx="6" fill="#344054" />
          <rect x="82" y="50" width="156" height="72" rx="3" fill="#d0d5dd" />
          <rect x="60" y="135" width="200" height="12" rx="3" fill="#475467" />
          <text x="160" y="168" textAnchor="middle" className="live-look-caption" fontSize="12">
            Laptop
          </text>
        </svg>
      );
    case 'tablet':
      return (
        <svg {...common}>
          <rect className="live-look-bg" width="320" height="180" />
          <rect x="105" y="28" width="110" height="124" rx="12" fill="#1d2939" />
          <rect x="114" y="40" width="92" height="100" rx="4" fill="#d0d5dd" />
          <circle cx="160" cy="148" r="3" fill="#98a2b3" />
          <text x="160" y="168" textAnchor="middle" className="live-look-caption" fontSize="12">
            Tablet
          </text>
        </svg>
      );
    case 'phone':
      return (
        <svg {...common}>
          <rect className="live-look-bg" width="320" height="180" />
          <path
            d="M78 58c0-10 8-18 18-18h20c8 0 12 6 12 14v18H98c-11 0-20-6-20-14z"
            fill="#475467"
          />
          <rect x="118" y="52" width="120" height="96" rx="10" fill="#344054" />
          <rect x="132" y="66" width="92" height="36" rx="4" fill="#98a2b3" />
          {[0, 1, 2].map((row) =>
            [0, 1, 2].map((col) => (
              <circle
                key={`${row}-${col}`}
                cx={148 + col * 28}
                cy={118 + row * 18}
                r="5"
                fill="#667085"
              />
            )),
          )}
          <text x="160" y="168" textAnchor="middle" className="live-look-caption" fontSize="12">
            IP-phone · desk set
          </text>
        </svg>
      );
    case 'display':
      return (
        <svg {...common}>
          <rect className="live-look-bg" width="320" height="180" />
          <rect x="55" y="30" width="210" height="110" rx="6" fill="#344054" />
          <rect x="66" y="40" width="188" height="88" rx="3" fill="#98a2b3" />
          <rect x="140" y="140" width="40" height="10" fill="#667085" />
          <rect x="110" y="150" width="100" height="6" rx="2" fill="#475467" />
          <text x="160" y="172" textAnchor="middle" className="live-look-caption" fontSize="12">
            Display
          </text>
        </svg>
      );
    case 'printer':
      return (
        <svg {...common}>
          <rect className="live-look-bg" width="320" height="180" />
          <rect x="80" y="55" width="160" height="70" rx="6" fill="#475467" />
          <rect x="100" y="35" width="120" height="30" rx="4" fill="#667085" />
          <rect x="110" y="110" width="100" height="18" rx="2" fill="#d0d5dd" />
          <text x="160" y="160" textAnchor="middle" className="live-look-caption" fontSize="12">
            Printer
          </text>
        </svg>
      );
    case 'camera':
      return (
        <svg {...common}>
          <rect className="live-look-bg" width="320" height="180" />
          <rect x="120" y="55" width="80" height="55" rx="10" fill="#344054" />
          <circle cx="160" cy="82" r="18" fill="#98a2b3" />
          <circle cx="160" cy="82" r="8" fill="#1d2939" />
          <rect x="150" y="110" width="20" height="25" fill="#667085" />
          <text x="160" y="160" textAnchor="middle" className="live-look-caption" fontSize="12">
            IP camera
          </text>
        </svg>
      );
    case 'nas':
      return (
        <svg {...common}>
          <rect className="live-look-bg" width="320" height="180" />
          <rect x="95" y="40" width="130" height="100" rx="6" fill="#344054" />
          {[0, 1, 2, 3].map((i) => (
            <rect key={i} x="110" y={55 + i * 18} width="100" height="12" rx="2" fill="#667085" />
          ))}
          <text x="160" y="160" textAnchor="middle" className="live-look-caption" fontSize="12">
            NAS
          </text>
        </svg>
      );
    case 'modem':
      return (
        <svg {...common}>
          <rect className="live-look-bg" width="320" height="180" />
          <rect x="100" y="70" width="120" height="45" rx="8" fill="#475467" />
          <rect x="150" y="40" width="8" height="32" rx="3" fill="#98a2b3" />
          <rect x="162" y="48" width="8" height="24" rx="3" fill="#98a2b3" />
          <circle cx="120" cy="92" r="4" fill="#12b76a" />
          <text x="160" y="150" textAnchor="middle" className="live-look-caption" fontSize="12">
            Modem / CPE
          </text>
        </svg>
      );
    case 'starlink':
      return (
        <svg {...common}>
          <rect className="live-look-bg" width="320" height="180" />
          <ellipse cx="160" cy="148" rx="46" ry="10" fill="#475467" opacity="0.45" />
          <rect x="152" y="108" width="16" height="36" rx="3" fill="#667085" />
          <rect
            x="108"
            y="42"
            width="104"
            height="72"
            rx="10"
            fill="#344054"
            transform="rotate(-18 160 78)"
          />
          <rect
            x="118"
            y="52"
            width="84"
            height="52"
            rx="6"
            fill="#98a2b3"
            transform="rotate(-18 160 78)"
          />
          <circle cx="188" cy="58" r="4" fill="#12b76a" />
          <text x="160" y="168" textAnchor="middle" className="live-look-caption" fontSize="12">
            Starlink · Gen3 dish
          </text>
        </svg>
      );
    case 'lte_dish':
      return (
        <svg {...common}>
          <rect className="live-look-bg" width="320" height="180" />
          <ellipse cx="160" cy="156" rx="28" ry="7" fill="#475467" opacity="0.45" />
          <rect x="154" y="96" width="12" height="58" rx="3" fill="#667085" />
          <ellipse
            cx="160"
            cy="78"
            rx="54"
            ry="38"
            fill="#344054"
            transform="rotate(-22 160 78)"
          />
          <ellipse
            cx="160"
            cy="78"
            rx="38"
            ry="24"
            fill="#98a2b3"
            transform="rotate(-22 160 78)"
          />
          <circle cx="178" cy="64" r="5" fill="#12b76a" />
          <text x="160" y="172" textAnchor="middle" className="live-look-caption" fontSize="12">
            LTE dish / MIMO
          </text>
        </svg>
      );
    case 'ups':
      return (
        <svg {...common}>
          <rect className="live-look-bg" width="320" height="180" />
          <rect x="90" y="50" width="140" height="80" rx="6" fill="#344054" />
          <rect x="110" y="70" width="40" height="24" rx="3" fill="#12b76a" />
          <rect x="170" y="70" width="40" height="24" rx="3" fill="#f79009" />
          <text x="160" y="160" textAnchor="middle" className="live-look-caption" fontSize="12">
            UPS
          </text>
        </svg>
      );
    case 'power_station':
      return (
        <svg {...common}>
          <rect className="live-look-bg" width="320" height="180" />
          <rect x="95" y="28" width="130" height="118" rx="10" fill="#1d2939" stroke="#98a2b3" strokeWidth="2" />
          <rect x="128" y="42" width="64" height="28" rx="4" fill="#344054" stroke="#667085" />
          <circle cx="160" cy="82" r="6" fill="none" stroke="#98a2b3" strokeWidth="2" />
          <path d="M160 76v6" stroke="#98a2b3" strokeWidth="2" strokeLinecap="round" />
          <rect x="108" y="48" width="12" height="8" rx="1.5" fill="#667085" />
          <rect x="108" y="60" width="12" height="8" rx="1.5" fill="#667085" />
          <circle cx="210" cy="54" r="8" fill="none" stroke="#98a2b3" strokeWidth="2" />
          <circle cx="204" cy="70" r="3.5" fill="#667085" />
          <circle cx="216" cy="70" r="3.5" fill="#667085" />
          <circle cx="130" cy="118" r="12" fill="none" stroke="#d0d5dd" strokeWidth="2" />
          <circle cx="160" cy="118" r="12" fill="none" stroke="#d0d5dd" strokeWidth="2" />
          <circle cx="190" cy="118" r="12" fill="none" stroke="#d0d5dd" strokeWidth="2" />
          <path
            d="M125 112v12M135 112v12M155 112v12M165 112v12M185 112v12M195 112v12"
            stroke="#98a2b3"
            strokeWidth="2"
            strokeLinecap="round"
          />
          <text x="160" y="168" textAnchor="middle" className="live-look-caption" fontSize="12">
            Зарядна станція
          </text>
        </svg>
      );
    default:
      return (
        <svg {...common}>
          <rect className="live-look-bg" width="320" height="180" />
          <rect x="110" y="50" width="100" height="80" rx="12" fill="#98a2b3" />
          <text x="160" y="150" textAnchor="middle" className="live-look-caption" fontSize="12">
            Device
          </text>
        </svg>
      );
  }
}
